<template>
  <div id="TableScales" class="Table" :key="renderKey">
    <div class="table__header">
      <div>
        <h6><b>Total rows with errors:</b> {{ totalErrors }}</h6>
      </div>
      <div class="table__end-align">
        <b-overlay
          :show="isLoading"
          rounded
          opacity="0.6"
          spinner-small
          class="d-inline-block"
        >
          <b-button
            :disabled="isLoading"
            class="table__end-align--button"
            @click="sendScale()"
            ><b-icon icon="reply-fill" aria-hidden="true"></b-icon> Send
            Quote</b-button
          >
        </b-overlay>
      </div>
    </div>
    <b-table
      id="scales-table"
      :items="items"
      :fields="fields"
      :per-page="pageSize"
      :current-page="currentPage"
      show-empty
    >
      <template #empty="scope">
        <h6 class="emptyText">{{ scope.emptyText }}</h6>
      </template>
      <template #cell()="row">
        <b-form-input
          v-if="row.field.key !== 'index'"
          type="text"
          :value="row.value"
          v-model="row.value"
          :ref="row.field.key + row.index"
          @change="verifyFields(row)"
          :class="isFieldInvalid(row) ? 'input-invalid' : 'input-valid'"
        >
        </b-form-input>
        <div v-else>{{ row.item.index + 1 }}</div>
      </template>
      <template #cell(country_pickup)="row">
        <b-form-select
          v-model="row.value"
          :ref="row.field.key + row.index"
          :options="countriesOptions"
          @change="verifyFields(row)"
          :class="isFieldInvalid(row) ? 'input-invalid' : 'input-valid'"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Select a country--</b-form-select-option
            >
          </template>
          <template #default>
            <span>{{ row.value }}</span>
          </template>
        </b-form-select>
      </template>
      <template #cell(country_delivery)="row">
        <b-form-select
          v-model="row.value"
          :ref="row.field.key + row.index"
          :options="countriesOptions"
          @change="verifyFields(row)"
          :class="isFieldInvalid(row) ? 'input-invalid' : 'input-valid'"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Select a country--</b-form-select-option
            >
          </template>
          <template #default>
            <span>{{ row.value }}</span>
          </template>
        </b-form-select>
      </template>
    </b-table>
    <el-pagination
      background
      layout="prev, pager, next"
      :total="totalRows"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="handleCurrentChange"
    >
    </el-pagination>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import * as XLSX from 'xlsx';

export default {
  name: 'Table',
  props: {
    file: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      fields: [
        'index',
        'country_pickup',
        'zip_pickup',
        'country_delivery',
        'zip_delivery',
        'hu',
        'weight',
        'length',
        'width',
        'height',
      ],
      scalesJson: null,
      items: [],
      pageSize: 10,
      currentPage: 1,
      tableKey: 0,
      rows: 0,
      isLoading: false,
      totalErrors: 0,
      validations: {
        canadaZipCode: /^[A-Za-z]\d[A-Za-z]([ -]?\d[A-Za-z]?\d?)?$/,
        usZipCode: /^\d{5}(-\d{4})?$/,
        intNumber: /^[1-9]\d*$/,
        number: /^-?\d+(\.\d+)?$/,
        country: /^(US|MX|CA)$/i,
      },
      countriesOptions: [
        { value: 'US', text: 'US' },
        { value: 'MX', text: 'MX' },
        { value: 'CA', text: 'CA' },
      ],
      countries: ['US', 'MX', 'CA'],
      renderKey: 0,
      incompleteZipCodes: [],
    };
  },
  computed: {
    totalRows() {
      return this.items.length;
    },
  },
  watch: {
    file(newVal) {
      if (newVal instanceof FileList) {
        this.$message({
          showClose: true,
          type: 'success',
          message: 'The file has been successfully uploaded',
          showConfirmButton: false,
          duration: 2000,
        });
        this.readingFile({ target: newVal });
      } else if (newVal instanceof File) {
        this.$message({
          showClose: true,
          type: 'success',
          message: 'The file has been successfully uploaded',
          showConfirmButton: false,
          duration: 2000,
        });
        this.readingFile({ target: [newVal] });
      }
    },
  },
  mounted() {
    this.initScroll();
  },
  methods: {
    initScroll() {
      const scrollToTop = document.getElementById('TableScales');
      scrollToTop.scrollIntoView({ behavior: 'smooth', block: 'end' });
    },
    changeAvailablePage(pageNumber) {
      this.currentPage = pageNumber;
      // eslint-disable-next-line
      this.tableKey++;
    },
    handleCurrentChange(val) {
      this.changeAvailablePage(val);
    },
    async readingFile() {
      const reader = new FileReader();
      reader.readAsArrayBuffer(this.file);
      // eslint-disable-next-line func-names
      reader.onload = async function (event) {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        this.scalesJson = XLSX.utils.sheet_to_json(worksheet);
        this.normalizeScaleJson();
        await this.validateFile();
      }.bind(this);
      this.renderKey += 1;
    },
    normalizeScaleJson() {
      const toUpperKeys = ['country_pickup', 'country_delivery'];
      const toStringKeys = ['zip_pickup', 'zip_delivery'];
      this.scalesJson.forEach((element) => {
        toUpperKeys.forEach((key) => {
          if (key in element && typeof element[key] === 'string') {
            // eslint-disable-next-line no-param-reassign
            element[key] = element[key].toUpperCase();
          }
        });
        toStringKeys.forEach((key) => {
          const isCanadian = this.validations.canadaZipCode.test(element[key]);
          if (
            key in element
            && !isCanadian
            && typeof element[key] === 'number'
          ) {
            // eslint-disable-next-line no-param-reassign
            element[key] = String(element[key]);
          }
        });
      });
    },
    async saveIncompleteZipCodes(usZipcode, field, index) {
      if (usZipcode.length === 4) {
        this.incompleteZipCodes.push({
          index,
          field,
          usZipcode,
        });
      }
    },
    async insertZeroUsZipcodes() {
      if (this.incompleteZipCodes.length > 0) {
        const result = await this.insertZeroAlert();
        if (result.isConfirmed) {
          for (let i = 0; i < this.incompleteZipCodes.length; i += 1) {
            const { index } = this.incompleteZipCodes[i];
            const { field } = this.incompleteZipCodes[i];
            const { usZipcode } = this.incompleteZipCodes[i];
            this.scalesJson[index][field] = `0${usZipcode}`;
          }
        }
      }
    },
    async insertZeroAlert() {
      const result = await this.$swal({
        title: 'US Zipcode incomplete',
        // eslint-disable-next-line
        text: `There are one or more incomplete US zip codes. Do you want to insert a Zero at the beginning?`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
      });
      return result;
    },
    async validateFile() {
      this.items = [];
      let totalErrors = 0;
      this.scalesJson.forEach((element, index) => {
        const errorCordinates = {};
        this.isThereError = false;
        this.scalesJson[index].index = index;
        const requiredFields = ['hu', 'weight', 'height', 'length', 'width'];
        requiredFields.forEach((field) => {
          if (!element[field]) {
            errorCordinates[field] = false;
          }
          if (!this.validations.number.test(element[field])) {
            errorCordinates[field] = false;
          }
        });
        if (element.zip_delivery && element.zip_pickup) {
          const isValidZip = (zip) => this.validations.usZipCode.test(zip)
          || this.validations.canadaZipCode.test(zip);
          ['zip_delivery', 'zip_pickup'].forEach((field) => {
            this.saveIncompleteZipCodes(element[field], field, index);
            if (!isValidZip(element[field])) {
              errorCordinates[field] = false;
            }
          });
        } else if (element?.zip_delivery && !element?.zip_pickup) {
          errorCordinates.zip_pickup = false;
        } else if (element?.zip_pickup && !element?.zip_delivery) {
          errorCordinates.zip_delivery = false;
        }
        if (element.country_delivery && element.country_pickup) {
          const isValidCountry = (country) => this.validations.country.test(country);
          ['country_pickup', 'country_delivery'].forEach((field) => {
            if (!isValidCountry(element[field])) {
              errorCordinates[field] = false;
            }
          });
        } else if (element?.country_delivery && !element?.country_pickup) {
          errorCordinates.country_pickup = false;
        } else if (element?.country_pickup && !element?.country_delivery) {
          errorCordinates.country_delivery = false;
        }
        if (Object.keys(errorCordinates).length > 0) {
          this.items.push(element);
          // eslint-disable-next-line
          totalErrors ++;
        }
        this.totalErrors = totalErrors;
      });
      this.insertZeroUsZipcodes();
    },
    isFieldInvalid(row) {
      const fieldKey = row.field ? row.field.key : null;
      const fieldValue = row.value || '';
      if (!fieldValue && fieldKey !== 'index') return true;

      if (fieldKey.includes('zip')) {
        const isUs = this.validations.usZipCode.test(fieldValue);
        const isCanadian = this.validations.canadaZipCode.test(fieldValue);
        return !(isUs || isCanadian);
      }
      if (this.validations.intNumber.test(fieldValue)) return false;
      if (this.validations.number.test(fieldValue)) return false;
      if (this.validations.country.test(fieldValue)) return false;

      return true;
    },
    async verifyFields(row) {
      const fieldKey = row.field ? row.field.key : null;
      const fieldValue = row.value || '';
      const input = this.$refs[row.field.key + row.index].$el;
      input.classList.remove('input-valid', 'input-invalid');

      const isZipField = fieldKey.includes('zip');
      const isNumericField = ['hu', 'weight', 'height', 'length', 'width'].includes(fieldKey);
      const isCountryField = ['country_pickup', 'country_delivery'].includes(
        fieldKey,
      );

      if (isZipField) {
        const isValid = this.validations.canadaZipCode.test(fieldValue)
        || this.validations.usZipCode.test(fieldValue);
        input.classList.add(isValid ? 'input-valid' : 'input-invalid');
        if (isValid) this.scalesJson[row.item.index][fieldKey] = fieldValue;
        // Add zero at the beginning when the length of the value is 4
        if (fieldValue.length === 4
          && this.validations.number.test(fieldValue)) {
          this.modifyZipField(row);
        }
        return;
      }

      if (isNumericField) {
        const isValid = this.validations.number.test(fieldValue);
        input.classList.add(isValid ? 'input-valid' : 'input-invalid');
        if (isValid) this.scalesJson[row.item.index][fieldKey] = fieldValue;
        return;
      }

      if (isCountryField) {
        const isValid = this.validations.country.test(fieldValue);
        input.classList.add(isValid ? 'input-valid' : 'input-invalid');
        if (isValid) this.scalesJson[row.item.index][fieldKey] = fieldValue;
      }
    },
    async modifyZipField(row) {
      const input = this.$refs[row.field.key + row.index].$el;
      input.classList.remove('input-valid', 'input-invalid');
      const isPickupUS = row.item.country_pickup === 'US';
      const isDeliveryUS = row.item.country_delivery === 'US';
      const isPossiblyShortZip = isPickupUS || isDeliveryUS;

      if (isPossiblyShortZip) {
        // eslint-disable-next-line prefer-template
        const fullZipcode = '0' + row.value;
        const result = await this.$swal({
          title: 'US Zipcode Interpretation',
          text: `We interpreted the zipcode "${row.value}" as "${fullZipcode}". Please confirm.`,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
          input.classList.add('input-valid');
          this.scalesJson[row.item.index][row.field.key] = fullZipcode;
          this.renderKey += 1;
        } else {
          input.classList.add('input-invalid');
          this.scalesJson[row.item.index][row.field.key] = row.value;
        }
      } else {
        input.classList.add('input-invalid');
      }
    },
    async sendScale() {
      this.validateFile();
      if (this.totalErrors === 0) {
        this.isLoading = true;
        const hauls = this.haulsConstructor();
        this.$store.commit('scale/setHauls', hauls);
        const response = await this.$store.dispatch('scale/sendQuote');
        if (response.status === 202) {
          this.$swal({
            icon: 'success',
            title: 'The scale has been sent',
            showConfirmButton: false,
            timer: 2000,
          }).then(() => {
            this.$router.push({ name: 'Scales', params: { resetInfo: true } });
          });
        } else {
          this.$swal({
            title: 'Error',
            text: 'Please try again, some data did not comply with the format.',
            icon: 'warning',
            showConfirmButton: true,
            confirmButtonColor: '#e53935',
          });
        }
        this.isLoading = false;
      } else {
        this.$swal({
          title: 'Error',
          text: 'Please resolve all fields problems',
          icon: 'warning',
          showConfirmButton: true,
          confirmButtonColor: '#e53935',
        });
      }
    },

    haulsConstructor() {
      const hauls = [];
      this.scalesJson.forEach((element) => {
        const haul = {};
        haul.line_items = [];
        const lineItems = {
          weight: element.weight,
          length: element.length,
          width: element.width,
          height: element.height,
          hu_count: element.hu,
        };
        haul.order = hauls.length + 1;
        if (
          (!element.zip_pickup && !element.zip_delivery)
          || (!element.country_pickup && !element.country_delivery)
        ) {
          hauls[hauls.length - 1].line_items.push(lineItems);
        } else {
          haul.zip_from = element.zip_pickup.toString().toUpperCase();
          haul.zip_to = element.zip_delivery.toString().toUpperCase();
          haul.country_from = element.country_pickup.toString().toUpperCase();
          haul.country_to = element.country_delivery.toString().toUpperCase();
          haul.line_items.push(lineItems);
          hauls.push(haul);
        }
      });
      return hauls;
    },
  },
};
</script>

<style scoped lang="scss">
.table {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__end-align {
    display: flex;
    justify-content: end;
    margin-top: 2rem;
    &--button {
      @include primary-button;
      margin: 30px;
    }
  }
}
.input-valid {
  border: 1px solid #00B900 !important;
}

.input-invalid {
  border: 1px solid #F44336 !important;
}
.emptyText {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
:deep {
  .custom-select.is-valid {
    background: url("data:image/svg+xml,%3csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%224%22 height=%225%22 viewBox=%220 0 4 5%22%3e%3cpath fill=%22234a40%22 d=%22M2 0L0 2h4zm0 5L0 3h4z%22/%3e%3c/svg%3e")
        right 0.75rem center / 8px 10px no-repeat,
      #fff;
  }
}
</style>
